import React from 'react';
import { Button, useTheme } from '@mui/material';
import { ADMIN_GRADIENTS, ADMIN_COLORS } from './glassTokens';

/**
 * GradientButton — three visual variants for the admin design system.
 *
 * Props:
 *   variant  — 'primary' | 'ghost' | 'danger'   (default: 'primary')
 *   All standard MUI Button props pass through.
 */
export default function GradientButton({
  variant = 'primary',
  sx = {},
  children,
  ...rest
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const styles = {
    primary: {
      background: ADMIN_GRADIENTS.accentShort,
      color: '#fff',
      border: 'none',
      boxShadow: isDark
        ? '0 4px 16px rgba(99,102,241,0.35)'
        : '0 4px 16px rgba(99,102,241,0.25)',
      '&:hover': {
        background: ADMIN_GRADIENTS.accent,
        boxShadow: isDark
          ? '0 6px 24px rgba(99,102,241,0.50)'
          : '0 6px 24px rgba(99,102,241,0.35)',
        transform: 'translateY(-1px)',
      },
      '&:active': { transform: 'translateY(0)' },
      '&.Mui-disabled': { background: 'rgba(99,102,241,0.3)', color: 'rgba(255,255,255,0.5)' },
    },
    ghost: {
      background: 'transparent',
      color: isDark ? 'rgba(255,255,255,0.85)' : '#4F46E5',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(99,102,241,0.30)'}`,
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      '&:hover': {
        background: isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.06)',
        borderColor: isDark ? 'rgba(99,102,241,0.60)' : 'rgba(99,102,241,0.50)',
        transform: 'translateY(-1px)',
      },
      '&:active': { transform: 'translateY(0)' },
    },
    danger: {
      background: ADMIN_GRADIENTS.danger,
      color: '#fff',
      border: 'none',
      boxShadow: '0 4px 16px rgba(244,63,94,0.30)',
      '&:hover': {
        background: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 100%)',
        boxShadow: '0 6px 24px rgba(244,63,94,0.45)',
        transform: 'translateY(-1px)',
      },
      '&:active': { transform: 'translateY(0)' },
      '&.Mui-disabled': { background: 'rgba(244,63,94,0.3)', color: 'rgba(255,255,255,0.5)' },
    },
  };

  return (
    <Button
      disableRipple={false}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.01em',
        borderRadius: '10px',
        transition: 'all 0.18s ease',
        ...styles[variant],
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}
